import { Box } from "@mantine/core";
import { Text, type TextProps } from "@mantine/core";
import { Title } from "@mantine/core";
import { List } from "@mantine/core";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { type Components } from "react-markdown";
import React, { useMemo } from "react";

// Define a type for iframe props
interface IframeComponentProps {
  src?: string;
  node?: unknown; // react-markdown specific prop, using unknown instead of any
  allowFullScreen?: boolean;
  frameBorder?: string | number;
  style?: React.CSSProperties;
}

const EmElmt = (props: React.HTMLAttributes<HTMLElement>) => <em style={{ fontStyle: "italic" }} {...props} />;

const StrongElmt = (props: React.HTMLAttributes<HTMLElement>) => <strong style={{ fontWeight: "bold" }} {...props} />;

const markdownComponents: Components = {
  h1: (props) => <Title {...props} order={2} style={{ marginBottom: "0.5rem", marginTop: "1rem" }} />,
  h2: (props) => <Title {...props} order={3} style={{ marginBottom: "0.5rem", marginTop: "1rem" }} />,
  h3: (props) => <Title {...props} order={4} style={{ marginBottom: "0.5rem", marginTop: "1rem" }} />,
  h4: (props) => <Title {...props} order={5} style={{ marginTop: "1.5rem" }} />,
  ul: (props) => (
    <List type="unordered" withPadding style={{ paddingLeft: "1rem", marginBottom: "0.5rem" }} {...props} />
  ),
  ol: (props) => {
    const { ...restProps } = props as React.HTMLAttributes<HTMLUListElement>;
    return <List type="ordered" withPadding style={{ paddingLeft: "1rem", marginBottom: "0.5rem" }} {...restProps} />;
  },
  em: (props) => <EmElmt {...props} />,
  strong: (props) => <StrongElmt {...props} />,
  li: (props) => <List.Item style={{ paddingBottom: "0.25rem" }} {...props} />,
  br: () => <div />,
  iframe: ({
    src: initialSrc,
    allowFullScreen: initialAllowFullScreen = true,
    frameBorder: initialFrameBorder = "0",
    ...restProps
  }: IframeComponentProps) => {
    let currentSrc = initialSrc;
    if (currentSrc && currentSrc.includes("youtube.com/embed")) {
      currentSrc = currentSrc.replace("youtube.com", "youtube-nocookie.com");
      try {
        const url = new URL(currentSrc);
        url.searchParams.set("controls", "0");
        url.searchParams.set("showinfo", "0");
        url.searchParams.set("modestbranding", "1");
        currentSrc = url.toString();
      } catch (error) {
        console.error("Error parsing iframe src URL:", error);
      }
    }

    return (
      <iframe
        src={currentSrc}
        allowFullScreen={initialAllowFullScreen}
        frameBorder={initialFrameBorder}
        style={{
          aspectRatio: "16/9",
          borderRadius: "12px",
          margin: "1rem 0",
          width: "100%",
          ...restProps.style,
        }}
      />
    );
  },
};

function mergeMarkdownComponents(paragraphTextProps?: TextProps): Components {
  return {
    ...markdownComponents,
    p: (props) => (
      <Text {...paragraphTextProps} style={{ marginBottom: "0.5rem", ...paragraphTextProps?.style }} {...props} />
    ),
  };
}

export type StyledMarkdownProps = {
  content: string;
  /** Mantine `Text` props for markdown paragraph (`p`) nodes (e.g. `size`, `fz`, `fw`, `c`, `lh`). */
  paragraphTextProps?: TextProps;
};

export function StyledMarkdown({ content, paragraphTextProps }: StyledMarkdownProps) {
  const components = useMemo(() => mergeMarkdownComponents(paragraphTextProps), [paragraphTextProps]);

  return (
    <Box className="markdown">
      <Markdown components={components} remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
        {content}
      </Markdown>
    </Box>
  );
}
